/* Tomorrow-you letters — local-only delayed-read.
 *
 * The user writes a letter to a future self. The letter is encrypted at rest
 * in the vault and is sealed until its `arrivesAt` timestamp.
 *
 * The seal is honor-based, not adversarial: the user CAN break it early with
 * an explicit tap. We record that they did, without judgment. The point isn't
 * to gate access — it's to introduce a deliberate pause between writing and
 * re-reading, so the future self has a chance to actually arrive.
 *
 * Status values:
 *   - "sealed"   : not yet matured; UI shows envelope + arrival date.
 *   - "arrived"  : matured; readable; awaiting first open.
 *   - "read"     : opened on or after maturity.
 *   - "broken"   : opened before maturity (still readable; flagged kindly).
 */

import {
  isVaultSupported,
  vaultPut,
  vaultList,
  vaultDelete,
  STORE_LETTERS,
} from "@/lib/secureVault";

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

function computeStatus(letter, now = Date.now()) {
  if (letter.status === "broken") return "broken";
  if (letter.status === "read") return "read";
  const arrives = new Date(letter.arrivesAt).getTime();
  return now >= arrives ? "arrived" : "sealed";
}

export async function listLetters() {
  if (!isVaultSupported()) return [];
  const raw = await vaultList(STORE_LETTERS);
  const now = Date.now();
  // Annotate each with derived status; do not mutate persisted state here.
  return raw
    .map((l) => ({ ...l, derivedStatus: computeStatus(l, now) }))
    .sort((a, b) => {
      // Sealed letters surface near the top by arrival date; arrived/read by createdAt desc.
      const ad = new Date(a.arrivesAt).getTime();
      const bd = new Date(b.arrivesAt).getTime();
      return ad - bd;
    });
}

export async function getMaturedUnread() {
  const letters = await listLetters();
  return letters.filter(
    (l) => l.derivedStatus === "arrived" && l.status !== "read" && l.status !== "broken",
  );
}

export async function addLetter({ text, arrivesAt }) {
  if (!isVaultSupported()) throw new Error("vault-unavailable");
  const entry = {
    id: uid(),
    text: text.trim(),
    createdAt: new Date().toISOString(),
    arrivesAt: new Date(arrivesAt).toISOString(),
    status: "sealed",
    openedAt: null,
  };
  await vaultPut(STORE_LETTERS, entry.id, entry);
  return entry;
}

async function updateLetter(letter, patch) {
  const next = { ...letter, ...patch };
  // strip transient
  delete next.derivedStatus;
  await vaultPut(STORE_LETTERS, next.id, next);
  return next;
}

export async function markRead(letter) {
  const now = Date.now();
  const arrives = new Date(letter.arrivesAt).getTime();
  if (now < arrives) {
    return updateLetter(letter, {
      status: "broken",
      openedAt: new Date().toISOString(),
    });
  }
  return updateLetter(letter, {
    status: "read",
    openedAt: new Date().toISOString(),
  });
}

export async function deleteLetter(id) {
  await vaultDelete(STORE_LETTERS, id);
}

/* Quick presets the UI offers, kept here so the calendar logic stays
 * out of the React component. All times pin to 7:00 local — a soft morning
 * window, not a precise alarm. */
export function presetArrival(label, from = new Date()) {
  const d = new Date(from);
  d.setHours(7, 0, 0, 0);
  switch (label) {
    case "tomorrow":
      if (d <= from) d.setDate(d.getDate() + 1);
      else d.setDate(d.getDate() + 1);
      return d;
    case "in-3-days":
      d.setDate(d.getDate() + 3);
      return d;
    case "next-week":
      d.setDate(d.getDate() + 7);
      return d;
    case "in-a-month":
      d.setMonth(d.getMonth() + 1);
      return d;
    default:
      d.setDate(d.getDate() + 1);
      return d;
  }
}
